import type { ManagedRole } from "./types";

export interface PermissionRow {
  code: string;
  label: string;
  /** Only works from an email + authenticator (MFA) sign-in, never a PIN. */
  needsMfa: boolean;
}

export interface PermissionGroup {
  heading: string;
  rows: PermissionRow[];
}

// Plain-English names for the permission codes, in the order a kitchen thinks
// about them. needsMfa mirrors the server's aal2 checks (packages/application).
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    heading: "Printing",
    rows: [
      { code: "labels.print", label: "Print labels (Quick, Batch, Prep Lists, Buffet)", needsMfa: false },
      { code: "labels.reprint", label: "Reprint a label from Print History", needsMfa: true },
      { code: "prep_lists.manage", label: "Create and edit prep lists", needsMfa: false },
      { code: "buffet.manage", label: "Create and edit buffet menus", needsMfa: false },
      { code: "ppds_labels.manage", label: "Create and edit PPDS labels", needsMfa: true },
    ],
  },
  {
    heading: "Catalogue",
    rows: [
      { code: "products.view", label: "See the catalogue", needsMfa: false },
      { code: "products.create", label: "Add products", needsMfa: true },
      { code: "products.edit", label: "Edit products", needsMfa: true },
      { code: "products.archive", label: "Archive products", needsMfa: true },
      { code: "allergens.edit", label: "Change allergens", needsMfa: true },
      { code: "shelf_life.edit", label: "Change shelf lives", needsMfa: true },
      { code: "shelf_life.approve", label: "Approve shelf lives", needsMfa: true },
    ],
  },
  {
    heading: "Team and setup",
    rows: [
      { code: "departments.view", label: "See departments", needsMfa: false },
      { code: "departments.manage", label: "Create and edit departments", needsMfa: true },
      { code: "members.manage_lower_roles", label: "Manage junior team members", needsMfa: true },
      { code: "members.manage", label: "Manage any team member", needsMfa: true },
      { code: "roles.manage", label: "Assign roles and edit what roles can do", needsMfa: true },
      { code: "printers.manage", label: "Set up printers and the Print Bridge", needsMfa: true },
      { code: "templates.publish", label: "Publish label templates", needsMfa: true },
      { code: "tenancy.manage", label: "Create sites and organisation settings", needsMfa: true },
    ],
  },
];

const KNOWN_CODES = new Set(PERMISSION_GROUPS.flatMap((group) => group.rows.map((row) => row.code)));

/** Active roles, most senior first, for the table's columns. */
export function rolesForTable(roles: readonly ManagedRole[]): ManagedRole[] {
  return roles.filter((role) => role.active).sort((a, b) => b.protected_level - a.protected_level || a.name.localeCompare(b.name));
}

/** Permission codes a role holds that the table has no plain-English row for. */
export function unlistedPermissions(roles: readonly ManagedRole[]): string[] {
  return [...new Set(roles.flatMap((role) => role.permissions))].filter((code) => !KNOWN_CODES.has(code)).sort();
}
